// /hr/bonusy-srazky — sloucena sekce Bonusy + Zalohy a srazky.
//
// Workflow:
//   Manager prida → stav 'navrh' (vzdy)
//   Owner prida   → muze rovnou cilovy stav (preskoci navrh)
//   Owner hromadne schvaluje typicky posledni ctvrtek v mesici
//
// Layout:
//   1) Gradient header + Pridat bonus / Pridat srazku
//   2) KPI: Ceka schvaleni | Ceka splneni | Ceka vyplaceni | Vyplaceno za mesic (netto)
//   3) Filtry: Typ pills + Stav pills + Mesic + Osoba + Fulltext
//   4) Bulk action bar (kdyz je neco vybrano)
//   5) Tabulka s checkboxy, barevny badge per typ

import { confirmDialog } from "../../components/confirmHost.jsx";
import React, { useMemo, useState } from "react";
import { Award, Plus, RefreshCw, Search, Coins, AlertCircle, Check, X as XIcon, Clock, Trash2, ThumbsUp, ThumbsDown, CheckCheck, TrendingUp, TrendingDown, Loader2, FileText, ArrowLeftRight } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { DlazdiceFiltr, EmptyState, SkeletonRows, Modal, HlavickaOkna, PruhFiltru, Segment, FiltrRadek } from "../../components/ui.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { apiFetch, useApiData } from "../../lib/api.js";
import { useDemoMode } from "../../lib/DemoModeContext.jsx";
import ZalozkaLink from "../../components/ZalozkaLink.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import InfoTip from "../../components/InfoTip.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import HRBonusSrazkaAddModal from "../../components/HRBonusSrazkaAddModal.jsx";
import HRBonusSrazkaVyplatitModal from "../../components/HRBonusSrazkaVyplatitModal.jsx";
import { formatDate } from "../../lib/format.js";
import { toast } from "../../components/Toast.jsx";

const MONTHS_CS = ["Leden","Únor","Březen","Duben","Květen","Červen","Červenec","Srpen","Září","Říjen","Listopad","Prosinec"];
function mesicLabel(m) {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || "—";
  const [y, mm] = m.split("-");
  return `${MONTHS_CS[parseInt(mm, 10) - 1]} ${y}`;
}
function fmt(n) { return new Intl.NumberFormat("cs-CZ").format(Math.round(n || 0)); }
function normalize(s) {
  return String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
}

// 28.5.2026 v3: 6 stavu workflow.
// caka_splneni → splneno_caka_schvaleni → caka_vyplaceni → vyplacen
// Z kazdeho meziktroku lze do stornovan; splneno_caka_schvaleni navic do neschvaleno.
const STAV_LABEL = {
  caka_splneni: "Čeká na splnění",
  splneno_caka_schvaleni: "Splněno, čeká na schválení",
  caka_vyplaceni: "Čeká na vyplacení",
  vyplacen: "Vyplaceno",
  stornovan: "Stornováno",
  neschvaleno: "Neschváleno",
};
const STAV_BADGE = {
  caka_splneni: "bg-sky-100 text-sky-800 border-sky-200",
  splneno_caka_schvaleni: "bg-amber-100 text-amber-800 border-amber-200",
  caka_vyplaceni: "bg-brand-100 text-brand-800 border-brand-200",
  vyplacen: "bg-emerald-100 text-emerald-800 border-emerald-200",
  stornovan: "bg-slate-200 text-slate-600 border-slate-300",
  neschvaleno: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function HRBonusySrazky() {
  const user = getCurrentUser();
  const isOwner = user?.role === "owner";
  const { blurred } = useDemoMode();

  const currentMonth = new Date().toISOString().substring(0, 7);
  const [mesic, setMesic] = useState(currentMonth);
  // 12.9.2026: staré cesty /hr/bonusy a /hr/zalohy-srazky přesměrovávají sem
  // s ?typ=bonus, resp. ?typ=srazka. Dřív se ten parametr nečetl, takže odkaz
  // přistál na nefiltrovaném seznamu. Teď nastaví výchozí filtr.
  const [typFilter, setTypFilter] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("typ");
    return t === "bonus" || t === "srazka" ? t : "vse";
  });                                                    // vse | bonus | srazka
  const [stavFilter, setStavFilter] = useState("aktivni"); // aktivni | caka_splneni | caka_vyplaceni | vyplacen | stornovan | vse
  const [personFilter, setPersonFilter] = useState("");   // person_id
  const [search, setSearch] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState("bonus");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [vyplatitOpen, setVyplatitOpen] = useState(false);
  const [vyplatitIds, setVyplatitIds] = useState([]); // co se vyplaci
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [reassignEntry, setReassignEntry] = useState(null);
  const [confirmZamitId, setConfirmZamitId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const url = `/api/hr-bs-list?typ=${typFilter}&stav=${stavFilter}&mesic=${mesic}${personFilter ? `&personId=${encodeURIComponent(personFilter)}` : ""}`;
  const { data, loading, error, refetch, refreshing } = useApiData(url);

  const entries = data?.entries || [];
  const summary = data?.summary || {};
  const byOsoba = data?.byOsoba || [];

  // Fulltext po backend filtru
  const visible = useMemo(() => {
    const q = normalize(search);
    if (!q) return entries;
    return entries.filter((e) => normalize(`${e.popis} ${e.display_name} ${e.poznamka || ""}`).includes(q));
  }, [entries, search]);

  // Vyber radky
  const allSelected = visible.length > 0 && visible.every((e) => selectedIds.has(e.id));
  const someSelected = selectedIds.size > 0;
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(visible.map((e) => e.id)));
  };
  const toggleOne = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };

  // Mesice pro select (od 1.6.2026 do aktualniho)
  const monthOptions = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < 18; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (m < "2026-06") break;
      arr.push({ value: m, label: mesicLabel(m) });
    }
    return arr;
  }, []);

  // Bulk akce
  const doBulk = async (action, extra = {}) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await apiFetch("/api/hr-bs-bulk", {
        method: "POST",
        body: JSON.stringify({ ids: Array.from(selectedIds), action, ...extra }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setSelectedIds(new Set());
      refetch();
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    } finally {
      setBulkBusy(false);
    }
  };

  // Single akce
  const doSingle = async (id, payload) => {
    try {
      const r = await apiFetch(`/api/hr-bs-patch?id=${id}`, { method: "PATCH", body: JSON.stringify(payload) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      refetch();
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    }
  };

  const doDelete = async (id) => {
    try {
      const r = await apiFetch(`/api/hr-bs-delete?id=${id}`, { method: "DELETE" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setConfirmDeleteId(null);
      refetch();
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    }
  };

  const openAdd = (typ) => { setAddType(typ); setAddOpen(true); };
  const openVyplatit = (ids) => { setVyplatitIds(ids); setVyplatitOpen(true); };

  return (
    <div className={`mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6 ${blurred ? "demo-blur" : ""}`}>
      {/* Header */}
      <GradientHeader
        title="Bonusy a srážky"
        icon={Award}
        napoveda="Sloučený log finančních bonusů a srážek. Manager i owner přidávají rovnou do Čeká na splnění (podmíněně) nebo Čeká na vyplacení (rovnou k proplacení). Owner pak schvaluje vyplacení typicky poslední čtvrtek v měsíci. Stornovat lze v každém stavu."
        akce={[
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing },
        ]}
      />

      {/* 12.9.2026: obě tlačítka byla v tmavé hlavičce. Hlavička nese podle
          vzorníku jen ikonové akce bez textu (rozhodnutí fáze 0), text v ní
          dělal druhé patro. Teď jsou pod ní. Dvě tlačítka s popiskem schválně:
          „přidat" tady znamená dvě různé věci a ikona plus by je nerozlišila.
          Na telefonu půl na půl, na počítači vedle sebe vlevo. */}
      <div className="grid grid-cols-2 gap-2 sm:flex">
        <button type="button" onClick={() => openAdd("bonus")} className="btn btn-primary w-full sm:w-auto">
          <Plus className="h-4 w-4" /> Bonus
        </button>
        <button type="button" onClick={() => openAdd("srazka")} className="btn btn-secondary w-full sm:w-auto">
          <Plus className="h-4 w-4" /> Srážka
        </button>
      </div>

      {/* Čísla nahoře zároveň filtrují podle stavu (rozhodnutí Č2). Netto je
          jen číslo, filtrovat se podle něj nedá. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <DlazdiceFiltr
          popisek="Čeká na splnění"
          hodnota={summary.caka_splneni || 0}
          ikona={AlertCircle}
          ton="info"
          info={<InfoTip text="Bonus nebo srážka, kde čekáme až nastane podmínka u klienta (například prodloužení, obrat nad limit, udržení klientů). Po splnění klikni Splněno, po nesplnění Stornovat." />}
          zapnuto={stavFilter === "caka_splneni"}
          onClick={() => setStavFilter(stavFilter === "caka_splneni" ? "aktivni" : "caka_splneni")}
        />
        <DlazdiceFiltr
          popisek="Splněno, čeká schválení"
          hodnota={summary.splneno_caka_schvaleni || (entries.filter((e) => e.stav === "splneno_caka_schvaleni").length)}
          ikona={Clock}
          ton="pozor"
          info={<InfoTip text="Položky čekající na schválení ownerem. Typicky owner schvaluje hromadně poslední čtvrtek v měsíci přes checkboxy a tlačítko Schválit vybrané." />}
          zapnuto={stavFilter === "splneno_caka_schvaleni"}
          onClick={() => setStavFilter(stavFilter === "splneno_caka_schvaleni" ? "aktivni" : "splneno_caka_schvaleni")}
        />
        <DlazdiceFiltr
          popisek="Čeká na vyplacení"
          hodnota={summary.caka_vyplaceni || 0}
          ikona={Coins}
          ton="znacka"
          info={<InfoTip text="Schválené položky čekající na vystavení reportu nebo výplaty pro daný měsíc. Po výplatě (typicky 1. dne následujícího měsíce) je owner označí jako vyplacené." />}
          zapnuto={stavFilter === "caka_vyplaceni"}
          onClick={() => setStavFilter(stavFilter === "caka_vyplaceni" ? "aktivni" : "caka_vyplaceni")}
        />
        <DlazdiceFiltr
          popisek={`Netto vše ${mesicLabel(mesic).toLowerCase()}`}
          hodnota={`${fmt((summary.bonusy_celkem || 0) - (summary.srazky_celkem || 0))} Kč`}
          ikona={TrendingUp}
          ton="ok"
          info={<InfoTip text="Netto bilance všech aktivních položek (krom stornovaných a neschválených) ve vybraném měsíci. Slouží jako odhad finální výplaty nebo fakturace pro tým." />}
        />
      </div>

      {/* Filtry ze vzorníku (rozhodnutí F1). */}
      <PruhFiltru
        zapnuto={typFilter !== "vse" || stavFilter !== "aktivni" || !!personFilter || !!search.trim()}
        onZrusit={() => { setTypFilter("vse"); setStavFilter("aktivni"); setPersonFilter(""); setSearch(""); }}
      >
        <Segment
          aria-label="Typ položky"
          value={typFilter}
          onChange={setTypFilter}
          items={[
            { key: "vse", label: "Vše" },
            { key: "bonus", label: "Bonusy", tonTextu: "text-emerald-700" },
            { key: "srazka", label: "Srážky", tonTextu: "text-rose-700" },
          ]}
        />
        <FiltrRadek
          stitek="Stav"
          vice={false}
          value={stavFilter}
          onChange={setStavFilter}
          items={[
            { key: "aktivni", label: "Aktivní" },
            { key: "caka_splneni", label: "Čeká splnění", ton: "info" },
            { key: "splneno_caka_schvaleni", label: "Čeká schválení", ton: "pozor" },
            { key: "caka_vyplaceni", label: "Čeká vyplacení", ton: "znacka" },
            { key: "vyplacen", label: "Vyplaceno", ton: "ok" },
            { key: "neschvaleno", label: "Neschváleno", ton: "problem" },
            { key: "stornovan", label: "Stornováno" },
            { key: "vse", label: "Vše" },
          ]}
        />
        <FiltrRadek
          stitek="Měsíc"
          vice={false}
          value={mesic}
          onChange={setMesic}
          items={monthOptions.map((o) => ({ key: o.value, label: o.label }))}
        />
        <FiltrRadek
          stitek="Osoba"
          vice={false}
          value={personFilter}
          onChange={(k) => setPersonFilter(k === personFilter ? "" : k)}
          popisVse="Všechny osoby"
          items={byOsoba.map((o) => ({ key: o.person_id, label: `${o.display_name} (${o.count})` }))}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Hledat popis nebo jméno…"
          className="field w-full"
        />
        <div className="text-xs text-slate-500">
          Zobrazeno <b className="tabular-nums text-slate-800">{visible.length}</b> z <b className="tabular-nums text-slate-800">{entries.length}</b>
        </div>
      </PruhFiltru>

      {/* Bulk action bar: manager vidi jen oznacit_splneno + stornovat, owner vse */}
      {someSelected && (
        <div className="bg-brand-50 border-2 border-brand-300 rounded-xl p-3 flex items-center gap-3 flex-wrap shadow-sm">
          <span className="text-sm font-semibold text-brand-900">
            Vybráno <b className="tabular-nums">{selectedIds.size}</b> položek
          </span>
          <div className="h-5 w-px bg-brand-300" />
          {/* manager+owner: Splneno (caka_splneni → splneno_caka_schvaleni) */}
          <button
            onClick={() => doBulk("oznacit_splneno")}
            disabled={bulkBusy}
            className="btn btn-primary"
            title="Pro Čeká na splnění: označit jako Splněno (přejde do Čeká na schválení)"
          >
            <Check className="w-4 h-4" /> Označit jako splněné
          </button>
          {/* jen owner: Schvalit, Neschvalit, Vyplatit */}
          {isOwner && (
            <>
              <button
                onClick={() => doBulk("schvalit")}
                disabled={bulkBusy}
                className="btn btn-primary"
                title="Schválit vybrané k vyplacení (Splněno čeká schválení → Čeká na vyplacení)"
              >
                <ThumbsUp className="w-4 h-4" /> Schválit
              </button>
              <button
                onClick={() => {
                  const duvod = prompt(`Důvod neschválení (povinný, ${selectedIds.size} položek):`);
                  if (duvod && duvod.trim()) doBulk("neschvalit", { duvod_storna: duvod.trim() });
                }}
                disabled={bulkBusy}
                className="btn btn-danger"
                title="Neschválit s důvodem (manager uvidí proč)"
              >
                <ThumbsDown className="w-4 h-4" /> Neschválit
              </button>
              <button
                onClick={() => openVyplatit(Array.from(selectedIds))}
                disabled={bulkBusy}
                className="btn btn-primary"
                title="Vyplatit (Čeká na vyplacení → Vyplaceno se zvoleným měsícem)"
              >
                <CheckCheck className="w-4 h-4" /> Vyplatit…
              </button>
            </>
          )}
          {/* manager+owner: Stornovat */}
          <button
            onClick={async () => {
              if (await confirmDialog(`Stornovat ${selectedIds.size} položek?`)) doBulk("stornovat");
            }}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-xl bg-slate-600 hover:bg-slate-500 text-white font-medium disabled:opacity-50"
          >
            <XIcon className="w-4 h-4" /> Stornovat
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="btn btn-secondary"
          >
            Zrušit výběr
          </button>
          {bulkBusy && <Loader2 className="w-4 h-4 animate-spin text-brand-600" />}
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">Chyba: {error}</div>}

      {loading && <SkeletonRows rows={6} />}

      {!loading && visible.length === 0 && (
        <div className="glass rounded-xl">
          <EmptyState
            icon={FileText}
            title="Nic neodpovídá filtru"
            hint="Zkus jiný měsíc, jiný stav nebo zrušit hledání."
          />
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Hlavička sloupců. Na telefonu není co popisovat, tam je z řádku karta. */}
          <div className="hidden grid-cols-12 items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 sm:grid">
            <div className="col-span-1 flex items-center">
              {isOwner && (
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 rounded border-slate-300" />
              )}
            </div>
            <div className="col-span-1">Typ</div>
            <div className="col-span-1">Datum</div>
            <div className="col-span-2">Osoba</div>
            <div className="col-span-2">Popis</div>
            <div className="col-span-1 text-right">Částka</div>
            <div className="col-span-2">Stav</div>
            <div className="col-span-2 text-right">Akce</div>
          </div>
          {visible.map((e) => (
            <Row
              key={e.id}
              entry={e}
              isOwner={isOwner}
              selected={selectedIds.has(e.id)}
              onToggle={() => toggleOne(e.id)}
              onSplneno={() => doSingle(e.id, { stav: "splneno_caka_schvaleni" })}
              onNesplneno={() => doSingle(e.id, { stav: "stornovan" })}
              onSchvalit={() => doSingle(e.id, { stav: "caka_vyplaceni" })}
              onNeschvalit={() => {
                const duvod = prompt("Důvod neschválení (povinný):");
                if (duvod && duvod.trim()) doSingle(e.id, { stav: "neschvaleno", duvod_storna: duvod.trim() });
              }}
              onVyplaceno={() => openVyplatit([e.id])}
              onStornovat={() => setConfirmZamitId(e.id)}
              onDelete={() => setConfirmDeleteId(e.id)}
              onReassign={() => setReassignEntry(e)}
            />
          ))}
        </div>
      )}

      {addOpen && (
        <HRBonusSrazkaAddModal
          defaultTyp={addType}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); refetch(); }}
        />
      )}

      {vyplatitOpen && (
        <HRBonusSrazkaVyplatitModal
          ids={vyplatitIds}
          defaultMesic={mesic}
          onClose={() => { setVyplatitOpen(false); setVyplatitIds([]); }}
          onSaved={() => { setVyplatitOpen(false); setVyplatitIds([]); setSelectedIds(new Set()); refetch(); }}
        />
      )}

      <ConfirmDialog
        open={confirmDeleteId != null}
        title="Smazat položku?"
        description="Smazání je nevratné a odstraní položku z logu. Pokud jen chceš označit jako neplatnou, použij Zamítnout."
        confirmLabel="Smazat"
        onConfirm={() => doDelete(confirmDeleteId)}
        onCancel={() => setConfirmDeleteId(null)}
        variant="danger"
      />

      <ConfirmDialog
        open={confirmZamitId != null}
        title="Zamítnout položku?"
        description="Položka přejde do stavu Stornováno a nezapočítá se do výplaty."
        confirmLabel="Zamítnout"
        onConfirm={() => { doSingle(confirmZamitId, { stav: "stornovan" }); setConfirmZamitId(null); }}
        onCancel={() => setConfirmZamitId(null)}
        variant="danger"
      />

      {reassignEntry && (
        <ReassignModal
          entry={reassignEntry}
          onClose={() => setReassignEntry(null)}
          onSaved={() => { setReassignEntry(null); refetch(); }}
        />
      )}
    </div>
  );
}

// Modal: prerazeni bonusu/srazky na jinou osobu.
function ReassignModal({ entry, onClose, onSaved }) {
  const { data } = useApiData("/api/hr-list-people?filter=vse&status=aktivni");
  const people = data?.people || [];
  const [personId, setPersonId] = useState("");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const qq = normalize(q);
    const list = people.filter((p) => p.id !== entry.person_id);
    if (!qq) return list;
    return list.filter((p) => normalize(`${p.display_name} ${p.pozice || ""}`).includes(qq));
  }, [people, q, entry.person_id]);

  const isBonus = entry.typ === "bonus";

  async function save() {
    if (!personId) return;
    setSaving(true);
    try {
      const r = await apiFetch(`/api/hr-bs-patch?id=${entry.id}`, { method: "PATCH", body: JSON.stringify({ person_id: personId }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onSaved();
    } catch (e) { toast("Chyba: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      plnaHlavicka
      header={<HlavickaOkna ikona={ArrowLeftRight} nazev={`Přeřadit ${isBonus ? "bonus" : "srážku"} na jinou osobu`} onClose={onClose} />}
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary">Zrušit</button>
          <button onClick={save} disabled={!personId || saving} className="btn btn-primary">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />} Přeřadit
          </button>
        </>
      }
    >
        <div className="space-y-3">
          <div className="text-sm bg-slate-50 border border-slate-200 rounded-xl p-3">
            <div className="font-semibold text-slate-800">{entry.popis}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Nyní: <span className="font-medium">{entry.display_name || entry.person_id}</span>, {isBonus ? "+" : "−"}{fmt(entry.castka)} Kč
            </div>
            {entry.sablona_kod === "auto_st_odecet" && (
              <div className="text-xs text-amber-700 mt-1.5 flex items-start gap-1">
                <span>⚙</span><span>Auto odečet tréninku: částka se po přeřazení přepočítá podle sazby nové osoby.</span>
              </div>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat osobu…"
              className="field w-full pl-9 pr-3" />
          </div>
          <div className="max-h-60 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-100">
            {filtered.map((p) => (
              <button key={p.id} type="button" onClick={() => setPersonId(p.id)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-brand-50 ${personId === p.id ? "bg-brand-50" : ""}`}>
                <span>
                  <span className="font-medium text-slate-800">{p.display_name}</span>
                  <span className="text-xs text-slate-400 ml-1">{p.pozice}</span>
                </span>
                {personId === p.id && <Check className="w-4 h-4 text-brand-600" />}
              </button>
            ))}
            {filtered.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-400">Žádná osoba.</div>}
          </div>
        </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────


function Row({ entry, isOwner, selected, onToggle, onSplneno, onNesplneno, onSchvalit, onNeschvalit, onVyplaceno, onStornovat, onDelete, onReassign }) {
  const uzka = useUzkaObrazovka();
  const isBonus = entry.typ === "bonus";
  const canReassign = isOwner && !["vyplacen", "stornovan", "neschvaleno"].includes(entry.stav);

  // Akce jsou stejné na telefonu i na počítači, jen se jinak zalomí.
  const akce = (
    <>
      {entry.stav === "caka_splneni" && (
        <>
          <PillBtn onClick={onSplneno} icon={Check} color="sky" title="Označit jako splněné (přejde do Čeká na schválení)">Splněno</PillBtn>
          <PillBtn onClick={onNesplneno} icon={XIcon} color="rose" title="Stornovat (podmínka nesplněna)">Nesplněno</PillBtn>
        </>
      )}
      {isOwner && entry.stav === "splneno_caka_schvaleni" && (
        <>
          <PillBtn onClick={onSchvalit} icon={ThumbsUp} color="violet" title="Schválit (přejde do Čeká na vyplacení)">Schválit</PillBtn>
          <PillBtn onClick={onNeschvalit} icon={ThumbsDown} color="rose" title="Neschválit s důvodem">Neschválit</PillBtn>
        </>
      )}
      {isOwner && entry.stav === "caka_vyplaceni" && (
        <>
          <PillBtn onClick={onVyplaceno} icon={CheckCheck} color="emerald" title="Schválit vyplacení (zvolíš měsíc proplacení)">Vyplatit</PillBtn>
          <PillBtn onClick={onStornovat} icon={XIcon} color="slate" title="Stornovat">Storno</PillBtn>
        </>
      )}
      {canReassign && (
        <button type="button" onClick={onReassign} title="Přeřadit na jinou osobu" aria-label="Přeřadit na jinou osobu"
          className="btn-ikona shrink-0">
          <ArrowLeftRight className="h-4 w-4" />
        </button>
      )}
      {isOwner && (
        <button type="button" onClick={onDelete} title="Smazat položku (nevratné)" aria-label="Smazat položku"
          className="btn-ikona shrink-0 border-rose-200 text-rose-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600">
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </>
  );

  if (uzka) {
    return (
      <div className={`border-b border-slate-100 px-4 py-3 last:border-b-0 ${selected ? "bg-brand-50/50" : ""}`}>
        <div className="flex items-start gap-2.5">
          {isOwner && (
            <input type="checkbox" checked={selected} onChange={onToggle} aria-label="Vybrat položku"
              className="mt-1 h-5 w-5 shrink-0 accent-brand" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-bold leading-snug text-slate-900 demo-sensitive">{entry.display_name || entry.person_id}</div>
            <div className="mt-0.5 text-[12px] text-slate-500">{entry.pozice || "—"}</div>
          </div>
          <span className={`shrink-0 whitespace-nowrap text-[17px] font-black tabular-nums ${isBonus ? "text-emerald-700" : "text-rose-700"}`}>
            {isBonus ? "+" : "−"}{fmt(entry.castka)} Kč
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-xs font-bold ${
            isBonus ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-rose-200 bg-rose-100 text-rose-800"
          }`}>
            {isBonus ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
            {isBonus ? "Bonus" : "Srážka"}
          </span>
          <span className={`inline-block rounded-lg border px-2 py-0.5 text-xs font-medium ${STAV_BADGE[entry.stav]}`}>
            {STAV_LABEL[entry.stav]}
          </span>
          {entry.sablona_kod === "auto_st_odecet" && (
            <span className="rounded-lg bg-slate-800 px-1.5 py-0.5 text-xs font-semibold text-white" title="Automaticky vytvořeno cronem při udělení ST bonusu klientovi">Auto</span>
          )}
          {entry.sablona_kod === "sales_zkusebni" && (
            <span className="rounded-lg bg-brand-100 px-1.5 py-0.5 text-xs font-semibold text-brand-deep" title="Vzniklo samo: klientka po zkušebním tréninku domluveném sales zaplatila spolupráci">Sales bonus</span>
          )}
          <span className="text-[12px] text-slate-500">{entry.datum_vzniku ? formatDate(entry.datum_vzniku) : "—"}</span>
        </div>

        <div className="mt-1.5 text-[13px] leading-snug text-slate-700 [overflow-wrap:anywhere]">
          {entry.popis}
          {entry.klient_name && (
            <div className="mt-0.5 text-[13px] font-medium">
              <span className="text-slate-500">Klient: </span>
              <ZalozkaLink
                to={`${((getCurrentUser()?._realRole || getCurrentUser()?.role || "").toLowerCase() === "manager") ? "/manager/clients" : "/clients"}?search=${encodeURIComponent(entry.klient_name)}&open=1`}
                title={entry.klient_name}
                className="text-brand-700 underline decoration-dotted underline-offset-2 hover:text-brand-900"
              >
                {entry.klient_name}
              </ZalozkaLink>
              {!entry.klient_id && <span className="ml-1 text-amber-600" title="Klient zatím není v DB">(nový)</span>}
            </div>
          )}
          {entry.poznamka && <div className="mt-0.5 text-[12.5px] italic text-slate-500">{entry.poznamka}</div>}
          {entry.duvod_storna && <div className="mt-0.5 text-[12.5px] text-rose-600">Důvod storna: {entry.duvod_storna}</div>}
          {entry.mesic_vyplaceni && <div className="mt-0.5 text-[12.5px] text-emerald-700">Vyplaceno za {mesicLabel(entry.mesic_vyplaceni)}</div>}
        </div>

        <div className="mt-1 text-[11.5px] text-slate-400">přidal {entry.navrhl_by?.split("@")[0] || "—"}</div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">{akce}</div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-12 gap-2 items-center px-4 py-2 border-b border-slate-100 last:border-b-0 transition-colors ${selected ? "bg-brand-50/50" : "hover:bg-slate-50"}`}>
      <div className="col-span-1 flex items-center">
        {isOwner && (
          <input type="checkbox" checked={selected} onChange={onToggle} className="w-4 h-4 rounded border-slate-300" />
        )}
      </div>
      <div className="col-span-1">
        <span className={`inline-flex items-center gap-1 text-sm px-2 py-0.5 rounded font-bold border ${
          isBonus ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-rose-100 text-rose-800 border-rose-200"
        }`}>
          {isBonus ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          {isBonus ? "Bonus" : "Srážka"}
        </span>
        {entry.sablona_kod === "auto_st_odecet" && (
          <div className="mt-1">
            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold bg-slate-800 text-white" title="Automaticky vytvořeno cronem při udělení ST bonusu klientovi">
              Auto
            </span>
          </div>
        )}
        {entry.sablona_kod === "sales_zkusebni" && (
          <div className="mt-1">
            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold bg-brand-100 text-brand-deep" title="Vzniklo samo: klientka po zkušebním tréninku domluveném sales zaplatila spolupráci">
              Sales bonus
            </span>
          </div>
        )}
      </div>
      <div className="col-span-1 text-sm text-slate-600">
        {entry.datum_vzniku ? formatDate(entry.datum_vzniku) : "—"}
      </div>
      <div className="col-span-2 min-w-0">
        <div className="text-base font-semibold text-slate-800 truncate demo-sensitive">{entry.display_name || entry.person_id}</div>
        <div className="text-xs text-slate-500 truncate">{entry.pozice || "—"}</div>
      </div>
      <div className="col-span-2 text-base text-slate-700 break-words">
        {entry.popis}
        {entry.klient_name && (
          <div className="text-sm mt-0.5 font-medium">
            <span className="text-slate-500">Klient: </span>
            <ZalozkaLink
              to={`${((getCurrentUser()?._realRole || getCurrentUser()?.role || "").toLowerCase() === "manager") ? "/manager/clients" : "/clients"}?search=${encodeURIComponent(entry.klient_name)}&open=1`}
              title={entry.klient_name}
              className="text-brand-700 hover:text-brand-900 underline decoration-dotted underline-offset-2"
            >
              {entry.klient_name}
            </ZalozkaLink>
            {!entry.klient_id && <span className="text-amber-600 ml-1" title="Klient zatím není v DB">(nový)</span>}
          </div>
        )}
        {entry.poznamka && <div className="text-sm text-slate-500 mt-0.5 italic">{entry.poznamka}</div>}
        {entry.duvod_storna && <div className="text-sm text-rose-600 mt-0.5">Důvod storna: {entry.duvod_storna}</div>}
        {entry.mesic_vyplaceni && <div className="text-sm text-emerald-700 mt-0.5">Vyplaceno za {mesicLabel(entry.mesic_vyplaceni)}</div>}
      </div>
      <div className="col-span-1 text-right">
        <span className={`text-lg font-bold tabular-nums ${isBonus ? "text-emerald-700" : "text-rose-700"}`}>
          {isBonus ? "+" : "−"}{fmt(entry.castka)}
        </span>
        <div className="text-xs text-slate-400">Kč</div>
      </div>
      <div className="col-span-2">
        <span className={`inline-block text-sm px-2 py-0.5 rounded font-medium border ${STAV_BADGE[entry.stav]}`}>
          {STAV_LABEL[entry.stav]}
        </span>
        <div className="text-xs text-slate-400 mt-0.5">přidal: {entry.navrhl_by?.split("@")[0] || "—"}</div>
      </div>
      <div className="col-span-2 flex flex-wrap items-center justify-end gap-1.5">{akce}
      </div>
    </div>
  );
}

// Pill button pro inline akci v tabulce. Ikona + text, kulate, barevne dle akce.
function PillBtn({ onClick, icon: Icon, color, children, title }) {
  const colorMap = {
    sky: "bg-sky-100 hover:bg-sky-200 text-sky-800 border-sky-200 hover:border-sky-300",
    rose: "bg-rose-100 hover:bg-rose-200 text-rose-800 border-rose-200 hover:border-rose-300",
    violet: "bg-brand-100 hover:bg-brand-200 text-brand-800 border-brand-200 hover:border-brand-300",
    emerald: "bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border-emerald-200 hover:border-emerald-300",
    slate: "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200 hover:border-slate-300",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors whitespace-nowrap ${colorMap[color] || colorMap.slate}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {children}
    </button>
  );
}
